import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { CalendarEvent } from './useCalendarEvents';

export interface GoogleCalendarStatus {
  connected: boolean;
  email: string | null;
  loading: boolean;
}

const SUPABASE_URL = import.meta.env.VITE_PUBLIC_SUPABASE_URL as string;

export const useGoogleCalendar = () => {
  const { user } = useAuth();
  const [status, setStatus] = useState<GoogleCalendarStatus>({
    connected: false,
    email: null,
    loading: true,
  });

  const fetchStatus = useCallback(async () => {
    if (!user) { setStatus({ connected: false, email: null, loading: false }); return; }
    const { data } = await supabase
      .from('profiles')
      .select('google_calendar_connected, google_calendar_email')
      .eq('id', user.id)
      .maybeSingle();
    setStatus({
      connected: data?.google_calendar_connected ?? false,
      email: data?.google_calendar_email ?? null,
      loading: false,
    });
  }, [user]);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  // Check for oauth callback result in URL
  useEffect(() => {
    const url = new URL(window.location.href);
    const gcal = url.searchParams.get('gcal');
    if (gcal === 'connected') {
      fetchStatus();
      url.searchParams.delete('gcal');
      window.history.replaceState({}, '', url.toString());
    }
  }, [fetchStatus]);

  const connect = () => {
    if (!user) return;
    const redirectUrl = `${SUPABASE_URL}/functions/v1/google-calendar-auth?user_id=${user.id}&redirect=${encodeURIComponent(window.location.origin + '/calendario?gcal=connected')}`;
    window.location.href = redirectUrl;
  };

  const disconnect = async () => {
    if (!user) return;
    await supabase
      .from('profiles')
      .update({
        google_calendar_connected: false,
        google_calendar_email: null,
        google_calendar_access_token: null,
        google_calendar_refresh_token: null,
        google_calendar_token_expiry: null,
      })
      .eq('id', user.id);
    setStatus({ connected: false, email: null, loading: false });
  };

  const syncEvent = async (event: CalendarEvent): Promise<{ success: boolean; message: string }> => {
    if (!user) return { success: false, message: 'Não autenticado.' };
    if (!status.connected) return { success: false, message: 'Google Calendar não conectado.' };

    const { data, error } = await supabase.functions.invoke('google-calendar-sync', {
      body: {
        user_id: user.id,
        event: {
          title: event.title,
          description: event.description ?? '',
          start_date: event.start_date,
          end_date: event.end_date ?? event.start_date,
          all_day: event.all_day ?? true,
          calendar_event_id: event.id,
          google_calendar_event_id: event.google_calendar_event_id,
        },
      },
    });

    if (error) return { success: false, message: `Erro na sincronização: ${error.message}` };
    return { success: true, message: data?.message ?? 'Evento enviado ao Google Calendar!' };
  };

  const syncBulk = async (events: CalendarEvent[]): Promise<{ success: boolean; message: string; count: number }> => {
    if (!user || !status.connected) return { success: false, message: 'Google Calendar não conectado.', count: 0 };
    let count = 0;
    for (const evt of events) {
      const result = await syncEvent(evt);
      if (result.success) count++;
    }
    return { success: true, message: `${count} evento(s) sincronizado(s) com o Google Calendar.`, count };
  };

  return { status, connect, disconnect, syncEvent, syncBulk, refetch: fetchStatus };
};
